import { Injectable } from '@angular/core'
import { FilterParams } from '../shared/utilities/http-params'
import { addMonths, format } from 'date-fns'
import { Observable, of } from 'rxjs'
import { IdrColDef, IdrColumnMetadata } from './idr-columns'

export type IdrColumnVisibility = Readonly<Record<string, boolean>>

const defaultFilterParams = (): readonly FilterParams[] => [{
  name: 'operating_date',
  values: [
    {
      operation: '>=',
      value: format(addMonths(new Date(), -2), 'yyyy-MM-dd'),
    },
  ],
}]

const isFilterParam = (value: unknown): boolean => {
  const { name, values } = value as FilterParams
  // noinspection SuspiciousTypeOfGuard
  return typeof name === 'string' && Array.isArray(values)
}

const localStorageKeys = {
  filterParams: 'idr_filter_params',
  columnVisibility: 'idr_column_visibility',
}

// ensure that the first filter is always operating_date
const validateDefaultFilterParams = (params: readonly FilterParams[]): readonly FilterParams[] => {
  if (params[0].name === 'operating_date') {
    return params
  } else {
    const operatingDateFilter = params.find(_ => _.name === 'operating_date') ?? defaultFilterParams()[0]
    return [
      operatingDateFilter,
      ...params.filter(_ => _.name !== 'operating_date'),
    ]
  }
}

const getFilterParamsOrDefault = (): readonly FilterParams[] => {
  const jsonValue = localStorage.getItem(localStorageKeys.filterParams)
  if (jsonValue) {
    try {
      const result = JSON.parse(jsonValue)
      if (Array.isArray(result) && result.length > 0 && result.every(isFilterParam)) {
        return validateDefaultFilterParams(result as readonly FilterParams[])
      }
    } catch {
      // fall through
    }
  }
  return defaultFilterParams()
}

@Injectable({ providedIn: 'root' })
export class IdrUserSettingsService {
  getFilters(): Observable<readonly FilterParams[]> {
    return of(getFilterParamsOrDefault())
  }

  saveFilters(params: readonly FilterParams[]) {
    localStorage.setItem(localStorageKeys.filterParams, JSON.stringify(params))
  }

  private parseColumnVisibility(): IdrColumnVisibility {
    const rawJson = localStorage[localStorageKeys.columnVisibility]
    if (rawJson) {
      try {
        const result = JSON.parse(rawJson)
        if (result && typeof result === 'object') {
          return result
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`malformed localStorage: ${localStorageKeys.columnVisibility} - ${rawJson}`, e)
      }
    }
    return {}
  }

  getColumnVisibility(): Observable<IdrColumnVisibility> {
    return of(this.parseColumnVisibility())
  }

  setColumnVisibility(colDefs: readonly IdrColDef[], metadata: IdrColumnMetadata) {
    const visibility: Record<string, boolean> = {}
    colDefs.forEach(c => {
      const defaultHide = metadata.columnSpecsByField[c.field].columnType === 'optional'
      if (c.hide !== defaultHide) {
        visibility[c.field] = !c.hide
      }
    })
    localStorage.setItem(localStorageKeys.columnVisibility, JSON.stringify(visibility))
  }
}
